import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Branding } from './Layout';

const ScratchCard = ({ match, onComplete, label, color }) => {
    const canvasRef = useRef(null);
    const [isScratched, setIsScratched] = useState(false);
    const [ctx, setCtx] = useState(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        // Fill with premium silver/gold gradient
        const grad = context.createLinearGradient(0, 0, canvas.width, canvas.height);
        grad.addColorStop(0, '#e2e8f0');
        grad.addColorStop(0.5, '#cbd5e1');
        grad.addColorStop(1, '#94a3b8');
        context.fillStyle = grad;
        context.fillRect(0, 0, canvas.width, canvas.height);

        // Add subtle pattern
        context.strokeStyle = 'rgba(255,255,255,0.2)';
        context.lineWidth = 1;
        for (let i = 0; i < canvas.width; i += 10) {
            context.beginPath();
            context.moveTo(i, 0);
            context.lineTo(i, canvas.height);
            context.stroke();
        }

        // Add text
        context.fillStyle = '#475569';
        context.font = '900 24px Inter, sans-serif';
        context.textAlign = 'center';
        context.fillText('GRATTA QUI', canvas.width / 2, canvas.height / 2 + 10);

        setCtx(context);
    }, []);

    const scratch = (x, y) => {
        if (!ctx || isScratched) return;

        ctx.globalCompositeOperation = 'destination-out';
        ctx.beginPath();
        ctx.arc(x, y, 60, 0, Math.PI * 2);
        ctx.fill();

        const imageData = ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height);
        const pixels = imageData.data;
        let transparentPixels = 0;
        for (let i = 3; i < pixels.length; i += 4) {
            if (pixels[i] === 0) transparentPixels++;
        }

        const percentage = (transparentPixels / (pixels.length / 4)) * 100;
        if (percentage > 45) {
            setIsScratched(true);
            if (onComplete) onComplete();
        }
    };

    const handleMouseMove = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        scratch((e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY);
    };

    const handleTouchMove = (e) => {
        e.preventDefault();
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const touch = e.touches[0];
        scratch((touch.clientX - rect.left) * scaleX, (touch.clientY - rect.top) * scaleY);
    };

    return (
        <div className="card glass-premium border-0 rounded-5 overflow-hidden mb-4 shadow-lg fade-in-up"
            style={{
                background: 'rgba(255, 255, 255, 0.4)',
                backdropFilter: 'blur(15px)',
                border: '1px solid rgba(255, 255, 255, 0.5) !important'
            }}>
            <div className="position-relative" style={{ height: '240px' }}>
                {/* Content */}
                <div className="absolute-inset d-flex flex-column justify-content-center p-3 p-md-4">
                    {/* Prima riga: Avatar + Tipo Match */}
                    <div className="d-flex align-items-center mb-3">
                        <div className="rounded-circle overflow-hidden shadow-sm flex-shrink-0 me-3" style={{ width: '65px', height: '65px', border: `3px solid ${color || '#db2777'}` }}>
                            {match?.photo ? (
                                <img src={match.photo} alt={match?.firstName || 'Match'} className="w-100 h-100 object-fit-cover" />
                            ) : (
                                <div className="w-100 h-100 d-flex align-items-center justify-content-center bg-light text-pink-500 fw-bold fs-2">
                                    {match?.firstName?.[0] || '?'}
                                </div>
                            )}
                        </div>
                        <span className={`badge bg-${color ? 'primary' : 'pink-600'} rounded-pill px-3 py-2 small fw-800`}>
                            {label || '❤️ È UN MATCH!'}
                        </span>
                    </div>

                    {/* Righe successive: Info */}
                    <div className="text-start overflow-hidden">
                        <h3 className="fw-900 mb-2 text-dark text-truncate">
                            {(match?.firstName || 'Utente')} {(match?.lastName || '')}
                            {match?.arrivalNumber && (
                                <span className="ms-2 badge bg-light text-pink-600 rounded-pill border border-pink-100" style={{ fontSize: '0.65rem', verticalAlign: 'middle' }}>
                                    #{match.arrivalNumber}
                                </span>
                            )}
                        </h3>

                        <div className="d-flex flex-column gap-1 overflow-hidden">
                            {match?.instagram && (
                                <div className="d-flex align-items-center gap-2 fw-700 text-truncate">
                                    <i className="fab fa-instagram text-pink-600" style={{ width: '18px' }}></i>
                                    <span className="text-pink-600">@{match.instagram.replace('@', '')}</span>
                                </div>
                            )}
                            {match?.facebook && (
                                <div className="d-flex align-items-center gap-2 fw-700 text-truncate">
                                    <i className="fab fa-facebook text-primary" style={{ width: '18px' }}></i>
                                    <span className="text-primary text-truncate">
                                        {match.facebook.replace(/https?:\/\/(www\.)?facebook\.com\//, '') || 'Facebook'}
                                    </span>
                                </div>
                            )}
                            {match?.phone && (
                                <div className="d-flex align-items-center gap-2 fw-700 text-truncate">
                                    <i className="fas fa-phone text-success" style={{ width: '18px' }}></i>
                                    <span className="text-success">{match.phone}</span>
                                </div>
                            )}
                            {match?.email && (
                                <div className="d-flex align-items-center gap-2 fw-700 text-truncate">
                                    <i className="fas fa-envelope text-muted" style={{ width: '18px' }}></i>
                                    <span className="text-muted small text-truncate">
                                        {match.email}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Cover */}
                {!isScratched && (
                    <canvas
                        ref={canvasRef}
                        width={800}
                        height={240}
                        onMouseMove={handleMouseMove}
                        onTouchMove={handleTouchMove}
                        className="position-absolute top-0 start-0 w-100 h-100 cursor-crosshair transition-all shadow-inner"
                        style={{ zIndex: 2 }}
                    />
                )}
            </div>
        </div>
    );
};

const ScratchMatches = ({ participation, onBack }) => {
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchResults = async () => {
            try {
                const res = await axios.get(`/speeddate/api/participants/results?token=${participation.token}`);
                setResults(res.data);
            } catch (err) {
                console.error("Error fetching matches:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchResults();
    }, [participation.token]);

    const sections = [
        { key: 'sentLikes', label: 'Like Inviati', color: '#6366f1' },
        { key: 'receivedLikes', label: 'Like Ricevuti', color: '#f43f5e' },
        { key: 'sentMagic', label: 'Magic Inviati', color: '#8b5cf6' },
        { key: 'receivedMagic', label: 'Magic Ricevuti', color: '#ec4899' },
        { key: 'matches', label: '❤️ MATCH!', color: '#db2777' },
        { key: 'superMatches', label: '🔥 SUPER MATCH!', color: '#be123c' }
    ];

    return (
        <div className="fade-in pb-5">
            <button onClick={onBack} className="btn btn-white rounded-pill px-4 py-2 mb-4 fw-800 shadow-sm border-0">
                <i className="fas fa-arrow-left me-2 text-pink-600"></i> Torna alla Dashboard
            </button>

            <div className="mx-auto w-100" style={{ maxWidth: '800px' }}>
                <div className="text-center mb-5">
                    <Branding />
                    <h1 className="fw-900 text-dark mt-4">Risultati dell'Evento</h1>
                    <p className="text-muted fw-600">Gratta i riquadri per scoprire i dettagli delle tue interazioni.</p>
                </div>

                {!loading && (
                    <div className="card glass-premium border-0 rounded-5 p-4 p-md-5 mb-5 shadow-lg text-center"
                        style={{ background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(10px)' }}>
                        <div className="mb-4">
                            <span className="badge bg-dark rounded-pill px-3 py-2 small fw-700 mb-2">
                                <i className="fas fa-calendar-check me-2"></i>
                                {participation?.Event?.name || 'Evento Speed Date'}
                            </span>
                        </div>

                        <div className="row g-4 justify-content-center">
                            <div className="col-6 col-md-4">
                                <div className="p-3 rounded-4 bg-white bg-opacity-50 shadow-sm border border-white">
                                    <div className="text-pink-600 fw-900 fs-2">{results?.stats?.likesSent || 0}</div>
                                    <div className="text-muted small fw-700 text-uppercase">Like Inviati</div>
                                </div>
                            </div>
                            <div className="col-6 col-md-4">
                                <div className="p-3 rounded-4 bg-white bg-opacity-50 shadow-sm border border-white">
                                    <div className="text-pink-600 fw-900 fs-2">{results?.stats?.likesReceived || 0}</div>
                                    <div className="text-muted small fw-700 text-uppercase">Like Ricevuti</div>
                                </div>
                            </div>
                            <div className="col-12 col-md-4">
                                <div className="p-3 rounded-4 bg-pink-600 text-white shadow-sm">
                                    <div className="fw-900 text-black fs-2">{results?.stats?.matchCount || 0}</div>
                                    <div className="text-muted small  fw-700 text-uppercase">Match Totali</div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-pink-500" role="status"></div>
                    </div>
                ) : (
                    <div className="d-flex flex-column gap-5">
                        {sections.map(section => {
                            const list = results?.[section.key] || [];
                            if (list.length === 0) return null;

                            return (
                                <div key={section.key} className="fade-in-up">
                                    <h3 className="fw-900 text-dark mb-4 px-3" style={{ fontSize: '1.25rem' }}>
                                        <i className="fas fa-star me-2" style={{ color: section.color }}></i>
                                        {section.label}
                                    </h3>
                                    {list.map((m, idx) => (
                                        <ScratchCard
                                            key={`${section.key}-${idx}`}
                                            match={m}
                                            label={section.label}
                                            color={section.color}
                                        />
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ScratchMatches;
